import React from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { green, grey, red } from "@mui/material/colors";

export const round1dp = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 10) / 10;

export const toLakhs = (value) => round1dp((Number(value) || 0) / 100000);

export const formatLakhs = (value) => `${toLakhs(value).toLocaleString("en-IN")}L`;

export const formatNumber = (value) => (Number(value) || 0).toLocaleString("en-IN");

export const growthPct = (current, base) => {
	const b = Number(base) || 0;
	if (!b) return null;
	return round1dp(((Number(current) || 0) - b) / b * 100);
};

/** Approval ladder — order matters for the funnel bar. */
export const STATUS_ORDER = [
	"PENDING",
	"IN PROGRESS",
	"SUBMITTED",
	"RM APPROVED",
	"NSM APPROVED",
	"FINAL APPROVED",
];

export const STATUS_COLORS = {
	PENDING: grey[400],
	"IN PROGRESS": "#ab47bc",
	SUBMITTED: "#f5a623",
	"RM APPROVED": "#42a5f5",
	"NSM APPROVED": "#5c6bc0",
	"FINAL APPROVED": "#43a047",
};

export const sectionLabelSx = {
	fontSize: 11,
	fontWeight: 700,
	letterSpacing: "0.08em",
	textTransform: "uppercase",
	color: grey[600],
};

export const cardSx = {
	bgcolor: "#fff",
	border: `1px solid ${grey[200]}`,
	borderRadius: 2.5,
	boxShadow: "0 4px 18px rgba(15, 23, 42, 0.045)",
};

/** Section wrapper — title strip + optional right-side controls. */
export const SectionCard = ({ title, subtitle, actions, children }) => (
	<Box sx={{ ...cardSx, p: 2, mb: 2 }}>
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			flexWrap="wrap"
			useFlexGap
			spacing={1.5}
			mb={1.5}
		>
			<Stack spacing={0.25}>
				<Typography sx={sectionLabelSx}>{title}</Typography>
				{subtitle ? (
					<Typography fontSize={12} color="text.secondary">
						{subtitle}
					</Typography>
				) : null}
			</Stack>
			{actions ? (
				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
					{actions}
				</Stack>
			) : null}
		</Stack>
		{children}
	</Box>
);

const Delta = ({ value, suffix = "%" }) => {
	if (value === null || value === undefined) return null;
	const positive = value >= 0;
	return (
		<Typography
			component="span"
			fontSize={11}
			fontWeight={700}
			color={positive ? green.A700 : red.A700}
		>
			{positive ? "+" : ""}
			{value}
			{suffix}
		</Typography>
	);
};

/** Compact KPI tile used in the headline strip. */
export const KpiTile = ({ label, value, hint, delta, deltaLabel, accent = grey[900] }) => (
	<Box
		sx={{
			...cardSx,
			px: 1.75,
			py: 1.25,
			display: "flex",
			flexDirection: "column",
			gap: 0.25,
			minWidth: 0,
		}}
	>
		<Typography sx={sectionLabelSx} noWrap>
			{label}
		</Typography>
		<Typography fontSize={20} fontWeight={700} color={accent} noWrap>
			{value}
		</Typography>
		<Stack direction="row" spacing={0.75} alignItems="center" sx={{ minHeight: 16 }}>
			<Delta value={delta} />
			{hint ? (
				<Typography fontSize={11} color="text.secondary" noWrap>
					{hint}
				</Typography>
			) : null}
			{deltaLabel && delta !== null && delta !== undefined ? (
				<Typography fontSize={11} color="text.secondary" noWrap>
					{deltaLabel}
				</Typography>
			) : null}
		</Stack>
	</Box>
);

/** Horizontal segmented bar showing store counts per approval stage. */
export const StatusFunnel = ({ counts, total }) => {
	const safeTotal = total || 0;
	if (!safeTotal) {
		return (
			<Typography fontSize={12} color="text.secondary">
				No stores match the current filters
			</Typography>
		);
	}

	return (
		<Stack spacing={1}>
			<Box
				sx={{
					display: "flex",
					width: "100%",
					height: 12,
					borderRadius: 6,
					overflow: "hidden",
					bgcolor: grey[100],
				}}
			>
				{STATUS_ORDER.map((status) => {
					const count = counts[status] || 0;
					if (!count) return null;
					return (
						<Tooltip
							key={status}
							title={`${status}: ${count} (${round1dp((count / safeTotal) * 100)}%)`}
						>
							<Box
								sx={{
									width: `${(count / safeTotal) * 100}%`,
									bgcolor: STATUS_COLORS[status],
								}}
							/>
						</Tooltip>
					);
				})}
			</Box>
			<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
				{STATUS_ORDER.map((status) => (
					<Stack key={status} direction="row" spacing={0.75} alignItems="center">
						<Box
							sx={{
								width: 8,
								height: 8,
								borderRadius: "50%",
								bgcolor: STATUS_COLORS[status],
							}}
						/>
						<Typography fontSize={11} fontWeight={600} color={grey[700]}>
							{status}
						</Typography>
						<Typography fontSize={11} color="text.secondary">
							{counts[status] || 0}
						</Typography>
					</Stack>
				))}
			</Stack>
		</Stack>
	);
};

/** Inline progress bar for per-store fill completion. */
export const FillBar = ({ value }) => {
	const pct = Math.max(0, Math.min(100, Number(value) || 0));
	return (
		<Stack direction="row" spacing={1} alignItems="center" width="100%">
			<Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: grey[200], overflow: "hidden" }}>
				<Box
					sx={{
						width: `${pct}%`,
						height: "100%",
						bgcolor: pct >= 100 ? green[500] : pct > 0 ? "#42a5f5" : grey[300],
					}}
				/>
			</Box>
			<Typography fontSize={11} fontWeight={600} color={grey[700]} sx={{ width: 34 }}>
				{pct}%
			</Typography>
		</Stack>
	);
};
